import client from '../../lib/api';

let cachedToken = null;
let guideRequest = null;

const errorDetail = (requestError) => {
  const data = requestError?.response?.data;
  if (!data) return '';
  if (typeof data === 'object') return data.detail || '';
  try {
    return JSON.parse(data)?.detail || '';
  } catch (parseError) {
    return '';
  }
};

const escapeAttribute = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const wrapGuideFragment = (fragment) => `<!doctype html>
<html lang="tr" data-theme="dark">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
</head>
<body>
${fragment}
</body>
</html>`;

// Yeni sekmedeki dış belge betik taşımaz. Asıl kılavuz, paneldeki eski
// görünümde olduğu gibi allow-same-origin verilmeyen sandbox içinde çalışır;
// böylece kendi içindekiler betiği yönetici oturumuna erişemez.
const wrapIsolatedTab = (fragment) => `<!doctype html>
<html lang="tr">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Yönetim Paneli Kullanım Kılavuzu</title>
<style>html,body,iframe{width:100%;height:100%;margin:0;border:0}body{overflow:hidden}</style>
</head>
<body>
<iframe title="Yönetim paneli kullanım kılavuzu" sandbox="allow-scripts" srcdoc="${escapeAttribute(wrapGuideFragment(fragment))}"></iframe>
</body>
</html>`;

const showMessage = (target, message) => {
  if (!target || target.closed) return;
  target.document.title = 'Yönetim Paneli Kullanım Kılavuzu';
  target.document.body.textContent = message;
};

const loadAdminGuide = () => {
  const token = localStorage.getItem('pusubet_token') || '';
  if (guideRequest && cachedToken === token) return guideRequest;

  cachedToken = token;
  const request = client
    .get('/admin/guide', { responseType: 'text' })
    .then(({ data }) => (typeof data === 'string' ? data : ''))
    .catch((requestError) => {
      // Geçici ağ hatası başarısız sonucu önbelleğe kilitlememeli. Bir sonraki
      // menü tıklaması ya da panel açılışı yeniden deneyebilsin.
      if (guideRequest === request) guideRequest = null;
      throw requestError;
    });
  guideRequest = request;
  return request;
};

/** Panel açılırken kılavuzu arka planda bir kez oturum belleğine alır. */
export const preloadAdminGuide = () => loadAdminGuide().then(
  () => true,
  () => false,
);

// Bölüm görünümünde kılavuzun üst çubuğu ve içindekiler rayı yoktur; ekran
// kartı çekmecenin zeminine oturur. Renkler kılavuzun kendi değişkenleridir.
const SECTION_STYLE = `
body{background:var(--surface); padding:0}
.screen{border:0; border-radius:0; box-shadow:none; margin:0; padding:20px 20px 40px; background:transparent}
.screen > h3:first-child{display:none}
.screen table{min-width:0}
@media (max-width:520px){
  .screen{padding:16px 16px 32px}
  .screen th,.screen td{padding:9px 8px; overflow-wrap:anywhere}
  .screen code{white-space:normal}
}
`;

/**
 * Kılavuzdan tek bir ekranın bölümünü (`<article id="...">`) yalıtılmış bir
 * belge olarak döndürür; bölüm yoksa boş metin.
 *
 * Ekranın yanında açılan yardım çekmecesi bunu sandbox'lı bir iframe'e
 * basar. İçerik ikinci kez yazılmaz: kılavuz tek kaynaktır ve frontend
 * paketine gömülmez (bkz. CLAUDE.md, *Yönetim paneli kullanım kılavuzu*).
 * Betikler bilinçli olarak alınmaz — bölümde içindekiler rayı yoktur ve
 * iframe `allow-scripts` taşımaz.
 */
export const loadAdminGuideSection = async (sectionId) => {
  const html = await loadAdminGuide();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const section = parsed.getElementById(sectionId);
  if (!section) return '';
  const styles = [...parsed.querySelectorAll('style')]
    .map((node) => node.textContent)
    .join('\n');
  const fonts = [...parsed.querySelectorAll('link[rel="stylesheet"]')]
    .map((node) => node.outerHTML)
    .join('\n');
  return `<!doctype html>
<html lang="tr" data-theme="dark">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${fonts}
<style>${styles}${SECTION_STYLE}</style>
</head>
<body>
${section.outerHTML}
</body>
</html>`;
};

/**
 * Kılavuzu menü tıklamasıyla doğrudan yeni sekmede açar.
 *
 * Sekme istekten önce, kullanıcı hareketinin içinde açılır; aksi hâlde
 * tarayıcının açılır pencere koruması async API yanıtından sonraki window.open
 * çağrısını engelleyebilir. Belge frontend paketine gömülmez ve yetkili istek
 * yine ortak axios istemcisinin Authorization başlığıyla yapılır.
 */
export const openAdminGuide = async () => {
  const guideTab = window.open('', '_blank');
  if (!guideTab) return false;

  guideTab.opener = null;
  showMessage(guideTab, 'Kullanım kılavuzu yükleniyor…');

  try {
    const data = await loadAdminGuide();
    if (guideTab.closed) return true;

    const blob = new Blob(
      [wrapIsolatedTab(data)],
      { type: 'text/html' },
    );
    const url = URL.createObjectURL(blob);
    guideTab.location.replace(url);
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch (requestError) {
    showMessage(
      guideTab,
      errorDetail(requestError) || 'Kullanım kılavuzu alınamadı.',
    );
    return false;
  }
};

export default openAdminGuide;
